import React from 'react';
import type { Id, Message } from '@norkin/chat-core';
import { MessageToolbar, StreamdownResponse } from '@norkin/chat-react';
import { Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { buildDefaultMessageActions } from './message-actions';

type BottomAnchorProps = {
  ref: React.RefObject<HTMLDivElement>;
};

type ChatMessageListProps = {
  threadId: Id;
  messages: Message[];
  className?: string;
  isStreaming?: boolean;
  emptyState?: React.ReactNode;
  bottomAnchorProps?: BottomAnchorProps;
};

const roleStyles: Record<string, string> = {
  user: 'bg-muted text-foreground border border-border',
  assistant: 'bg-background text-foreground',
  system: 'border-secondary/40 bg-secondary/40 text-secondary-foreground',
  tool: 'border-accent/50 bg-accent/50 text-accent-foreground',
};

const assistantMarkdownClassName =
  'space-y-3 text-sm leading-relaxed [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-3';

function AssistantMarkdown({
  messageId,
  className,
  markdown,
}: {
  messageId: Id;
  className?: string;
  markdown?: string;
}) {
  return (
    <StreamdownResponse
      messageId={messageId}
      markdown={markdown}
      className={className}
      streamdownProps={{ className }}
    />
  );
}

export function ChatMessageList({
  threadId,
  messages,
  className,
  isStreaming,
  emptyState,
  bottomAnchorProps,
}: ChatMessageListProps) {
  const visibleMessages = React.useMemo(
    () => messages.filter(message => !(message.role === 'assistant' && message.status === 'aborted')),
    [messages],
  );

  return (
    <div className={cn('space-y-1 p-4', className)}>
      {visibleMessages.length === 0
        ? emptyState ?? null
        : visibleMessages.map(message => (
            <MessageBubble key={message.id} message={message} threadId={threadId} />
          ))}
      {isStreaming ? <StreamingIndicator /> : null}
      {bottomAnchorProps ? <div id="chat-bottom-anchor" {...bottomAnchorProps} /> : null}
    </div>
  );
}

function MessageBubble({ message, threadId }: { message: Message; threadId: Id }) {
  const actions = React.useMemo(() => buildDefaultMessageActions(), []);
  const bubbleClass = roleStyles[message.role] ?? 'bg-background';
  const showRoleBadge = message.role !== 'user' && message.role !== 'assistant';
  const isUser = message.role === 'user';
  const resolvedThreadId = (message as Message & { threadId?: Id }).threadId ?? threadId;
  const contentElements =
    message.role === 'assistant' ? buildAssistantContent(message) : buildDefaultContent(message);

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div className={cn('group flex min-w-0 flex-col gap-2', isUser ? 'max-w-[80%]' : 'max-w-full')}>
        <div className={cn('space-y-2 rounded-lg p-4 shadow-sm [contain:layout_style]', bubbleClass, isUser ? 'text-right' : '')}>
          {showRoleBadge ? (
            <Badge variant="outline" className="uppercase tracking-wide">
              {message.role}
            </Badge>
          ) : null}
          <div className="space-y-3 text-sm leading-relaxed">
            {contentElements}
          </div>
        </div>
        <div
          className={cn(
            'flex opacity-0 pointer-events-none transition-opacity duration-150 group-hover:opacity-100 group-hover:pointer-events-auto',
            isUser ? 'justify-end' : 'justify-start',
          )}
        >
          <MessageToolbar
            threadId={resolvedThreadId}
            message={message}
            actions={actions}
            placement="below"
            className={cn('gap-2', isUser ? 'ml-auto' : undefined)}
          />
        </div>
      </div>
    </div>
  );
}

function StreamingIndicator() {
  return (
    <div className="flex items-center gap-2 rounded-md border border-dashed border-border/70 bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      <span>Assistant is responding...</span>
    </div>
  );
}

function safeJson(value: unknown) {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function formatPayload(value: unknown) {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function renderToolDetails({
  key,
  title,
  payload,
}: {
  key: string;
  title: string;
  payload: unknown;
}) {
  return (
    <details key={key} className="rounded-md border bg-background/80">
      <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </summary>
      <pre className="whitespace-pre-wrap break-all px-3 py-2 text-xs">{formatPayload(payload)}</pre>
    </details>
  );
}

function buildAssistantContent(message: Message) {
  const elements: React.ReactNode[] = [];
  let textBuffer = '';
  let textIndex = 0;

  const flushText = () => {
    if (!textBuffer) return;
    elements.push(
      <AssistantMarkdown
        key={`${message.id}-text-${textIndex}`}
        messageId={message.id}
        markdown={textBuffer}
        className={assistantMarkdownClassName}
      />,
    );
    textBuffer = '';
    textIndex += 1;
  };

  message.content.forEach((part, index) => {
    if (part.type === 'text') {
      textBuffer += part.text;
      return;
    }

    flushText();

    const key = `${message.id}-part-${index}`;
    if (part.type === 'tool-call') {
      elements.push(
        renderToolDetails({
          key,
          title: `Tool call · ${part.toolName ?? 'Unknown'}`,
          payload: safeJson(part.args ?? ''),
        }),
      );
      return;
    }
    if (part.type === 'tool-result') {
      elements.push(
        renderToolDetails({
          key,
          title: `Tool result · ${part.toolName ?? 'Unknown'}`,
          payload: safeJson(part.result ?? ''),
        }),
      );
    }
  });

  flushText();

  if (elements.length === 0) {
    return [
      <AssistantMarkdown
        key={`${message.id}-text-0`}
        messageId={message.id}
        className={assistantMarkdownClassName}
      />,
    ];
  }

  return elements;
}

function buildDefaultContent(message: Message) {
  return message.content.map((part, index) => {
    const key = `${message.id}-part-${index}`;
    if (part.type === 'text') {
      return (
        <p key={key} className="whitespace-pre-wrap">
          {part.text}
        </p>
      );
    }
    if (part.type === 'tool-call') {
      return renderToolDetails({
        key,
        title: `Tool call · ${part.toolName ?? 'Unknown'}`,
        payload: safeJson(part.args ?? ''),
      });
    }
    if (part.type === 'tool-result') {
      return renderToolDetails({
        key,
        title: `Tool result · ${part.toolName ?? 'Unknown'}`,
        payload: safeJson(part.result ?? ''),
      });
    }
    return null;
  });
}
